import React, { useEffect, useMemo, useCallback } from "react";
import { Card, Table, Button, Typography, Tag, Spin, Alert } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { reducer, sliceKey, actions } from "./slice";
import { tatMonitoringDetailedSaga } from "./saga";
import {
  selectTableData,
  selectLoading,
} from "./selectors";

const { Title } = Typography;

const columns = [
  {
    title: "Stage",
    dataIndex: "stage",
    key: "stage",
    render: (_, record) => {
      const { start, end } = record.stage || {};
      return `${start || ''} → ${end || ''}`;
    },
  },
  {
  title: "Current TAT",
  dataIndex: "currentTat",
  key: "currentTat",
  render: (text, record) => {
    const achievement = parseFloat(record.achievement);
    const isOnTrack = achievement >= 0;

    return (
      <span
        style={{
          fontWeight: "bold",
          color: isOnTrack ? "green" : "red",
        }}
      >
        {text}d
      </span>
    );
  },
},
{
  title: "Target TAT",
  dataIndex: "targetTat",
  key: "targetTat",
  render: (text) => (
    <span style={{ fontWeight: "bold" }}>{text}d</span>
  ),
},
  {
  title: "Achievement ",
  dataIndex: "achievement",
  key: "achievement",
  render: (text) => {
    const value = parseFloat(text);

    if (value < 0) {
      return <Tag color="red">Exceeds Allowed TAT</Tag>;
    } else {
      return <Tag color="green">On Track</Tag>;
    }
  },
}
  // {
  //   title: "Trend",
  //   dataIndex: "trend",
  //   key: "trend",
  //   render: value => {
  //     if (!value) return <span>-</span>;
  //     return (
  //       <span style={{ color: value.startsWith("↑") ? "red" : "green" }}>
  //         {value}
  //       </span>
  //     );
  //   },
  // },
];

const TATMonitoringDetailed = ({ filters = {} }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: tatMonitoringDetailedSaga });

  const loading = useSelector(selectLoading);
  const data = useSelector(selectTableData);
  const error = useSelector(state => state.tatMonitoringDetailed?.error);

  const memoizedFilters = useMemo(() => ({
    companyIds: filters.companyIds || [],
    projectCodeIds: filters.projectCodeIds || [],
    selectedState: filters.selectedState || [],
    sortDescending: filters.sortDescending ?? true
  }), [
    filters.companyIds,
    filters.projectCodeIds,
    filters.selectedState,
    filters.sortDescending
  ]);

  // Create stable callback for navigation
  const handleBackClick = useCallback(() => {
    history.push("/bo/dashboard", { filters: memoizedFilters });
  }, [history, memoizedFilters]);

  // Fetch data when memoized filters change
  useEffect(() => {
    dispatch(
      actions.fetchDetailed({
        filtersString: JSON.stringify(memoizedFilters),
      })
    );
  }, [dispatch, memoizedFilters]);

  // Memoized table data
  const tableData = useMemo(() => {
    if (!Array.isArray(data)) return [];
   
    return data.map((item, idx) => ({
      ...item,
      key: idx + 1
    }));
  }, [data]);

  return (
    <div>
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBackClick}>
            </Button>
            <Title level={4} style={{ margin: 0 }}>Complete TAT Analytics</Title>
          </div>
        }
        style={{ marginTop: 24 }}
      >
        {error && (
          <Alert
            message="Error loading data"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
       
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>Loading TAT data...</div>
          </div>
        ) : (
          <div>
            <Title level={5}>TAT Monitoring Details</Title>
            <Table
              columns={columns}
              dataSource={tableData}
              pagination={false}
              bordered
              scroll={{ x: true }}
              loading={loading}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default TATMonitoringDetailed;